import type { ReactNode } from 'react';

// Curriculum Markdown is rendered as React nodes. Raw HTML and unsafe URL schemes stay inert.
function safeHref(value: string): string | undefined {
  const href = value.trim();
  return /^(https?:\/\/|\/(?!\/)|#)/i.test(href) ? href : undefined;
}
function inline(text: string, allowImages = true): ReactNode[] {
  const pattern = /(!?\[[^\]]*\]\([^\s)]+\)|`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g;
  return text.split(pattern).map((part, index) => {
    const link = part.match(/^(!?)\[([^\]]*)\]\(([^)]+)\)$/);
    if (link) {
      const href = safeHref(link[3]);
      if (!href) return <span key={index}>{link[2]}</span>;
      if (link[1] && !allowImages) return <span key={index}>{link[2]}</span>;
      return link[1] ? (
        <img
          key={index}
          src={href}
          alt={link[2]}
          loading="lazy"
          className="my-3 max-h-80 max-w-full rounded-2xl object-contain"
        />
      ) : (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="break-words underline decoration-brand-coral underline-offset-4"
        >
          {link[2]}
        </a>
      );
    }
    if (part.startsWith('`'))
      return (
        <code key={index} className="rounded bg-surface px-1 break-words">
          {part.slice(1, -1)}
        </code>
      );
    if (part.startsWith('**')) return <strong key={index}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('*')) return <em key={index}>{part.slice(1, -1)}</em>;
    return part;
  });
}
export function CourseMarkdown({
  text,
  allowImages = true,
}: {
  text: string;
  allowImages?: boolean;
}) {
  const renderInline = (value: string) => inline(value, allowImages);
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  for (let i = 0; i < lines.length; ) {
    const key = i;
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }
    if (/^\s*```/.test(line)) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i])) code.push(lines[i++]);
      i++;
      blocks.push(
        <pre key={key} className="overflow-x-auto rounded-2xl bg-surface p-4 text-sm">
          <code>{code.join('\n')}</code>
        </pre>,
      );
      continue;
    }
    const heading = line.match(/^#{1,6}\s+(.+)$/);
    if (heading) {
      blocks.push(
        <h3 key={key} className="text-lg font-bold text-ink">
          {renderInline(heading[1])}
        </h3>,
      );
      i++;
      continue;
    }
    if (/^\s*(?:[-*+]\s|\d+\.\s)/.test(line)) {
      const ordered = /^\s*\d+\.\s/.test(line);
      const items: ReactNode[] = [];
      while (i < lines.length && (ordered ? /^\s*\d+\.\s/ : /^\s*[-*+]\s/).test(lines[i])) {
        items.push(
          <li key={i}>
            {renderInline(
              lines[i++].replace(/^\s*(?:[-*+]|\d+\.)\s+/, '').replace(/^\[ \]\s*/, ''),
            )}
          </li>,
        );
      }
      blocks.push(
        ordered ? (
          <ol key={key} start={parseInt(line.trim(), 10)} className="list-decimal space-y-2 pl-6">
            {items}
          </ol>
        ) : (
          <ul key={key} className="list-disc space-y-2 pl-6">
            {items}
          </ul>
        ),
      );
      continue;
    }
    if (/^\s*>/.test(line)) {
      blocks.push(
        <blockquote key={key} className="border-l-4 border-brand-mint pl-4">
          {renderInline(line.replace(/^\s*>\s?/, ''))}
        </blockquote>,
      );
      i++;
      continue;
    }
    if (/^\|/.test(line) && /^\|?\s*:?-{3}/.test(lines[i + 1] ?? '')) {
      const cells = (value: string) => value.replace(/^\||\|$/g, '').split('|');
      const headers = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /^\|/.test(lines[i])) rows.push(cells(lines[i++]));
      blocks.push(
        <div key={key} className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                {headers.map((cell, n) => (
                  <th key={n} className="border-b border-hairline p-2">
                    {renderInline(cell.trim())}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, n) => (
                <tr key={n}>
                  {row.map((cell, c) => (
                    <td key={c} className="border-b border-hairline p-2">
                      {renderInline(cell.trim())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    const paragraph = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^\s*(#{1,6}\s|```|[-*+]\s|\d+\.\s|>|\|)/.test(lines[i])
    )
      paragraph.push(lines[i++]);
    blocks.push(
      <div key={key} className="whitespace-pre-line break-words leading-relaxed">
        {renderInline(paragraph.join('\n'))}
      </div>,
    );
  }
  return (
    <div className="space-y-3 text-ink-soft" data-testid="course-markdown">
      {blocks}
    </div>
  );
}
